import { throwsNetworkError } from "@/utils/http/NetworkManagerMixin";
import { Component, Inject, Mixins } from "vue-property-decorator";
import JackocoinsNetworkManagerMixin from "@/mixins/JackocoinsNetworkMangerMixin";

import { EServices } from "@/types";
import { namespace } from "vuex-class";
import IIndexable from "@/utils/types/IIndexable";
import { IRank, IRate } from "../../types";
import VEmptyState from "@/vuetify-extensions/VEmptyState.vue";
import RatesClient from "../../services/RatesClient";
import { IAnnouncement } from "@/modules/dashboard/types";
import AnnouncementClient from "../../services/AnnoucementClient";
import RanksClient from "../../services/RanksClient";
import { ICustomer } from "@/modules/banks/types";
import KycDialog from "@/modules/Kyc/components/KycDialog.vue";
import KycClient from "@/modules/Kyc/services/KycClient";
import AuthClient from "@/modules/auth/services/AuthClient";
import { IKycRecord } from "@/modules/Kyc/types";

const SellModule = namespace("SellModule");
const AccountModule = namespace("AccountModule");
const KycModule = namespace("KycModule");

@Component({
  components: {
    VEmptyState,
    KycDialog,
  },
})
export default class Sell extends Mixins(JackocoinsNetworkManagerMixin) {
  message = "";
  customerRank: IRank | null = null;
  usdtValue: number = 0;
  maxRangeValue: number = 0;
  barValue: number = 0;
  color = "";
  bgColor = "";
  showDialog: boolean = false;
  dropdowns: IIndexable<boolean> = {};

  loading: IIndexable<boolean> = {
    getRates: true,
  };

  @Inject(EServices.rates)
  ratesClient!: RatesClient;

  @Inject(EServices.announcement)
  announcementClient!: AnnouncementClient;

  @Inject(EServices.ranks)
  ranksClient!: RanksClient;

  @Inject(EServices.kyc)
  kycClient!: KycClient;

  @Inject(EServices.auth)
  authClient!: AuthClient;

  @SellModule.State
  rates!: IRate[];

  @AccountModule.State
  customer!: ICustomer;

  @KycModule.State
  kyc!: IKycRecord;

  @throwsNetworkError()
  async getRates() {
    await this.ratesClient.getRates();
  }

  @SellModule.State
  announcement!: IIndexable<IAnnouncement>;

  get kycRecord() {
    return this.kyc && this.kyc.verificationStatus === "Completed";
  }

  get kycRequiredStatus() {
    return this.customer && this.customer.kycRequired === true;
  }

  get ratesDisplay() {
    const display = [];
    for (const rate of this.formattedRates) {
      let existing = display.findIndex(
        (d) => d.currencySymbol == rate.currency.symbol
      );
      if (existing < 0)
        existing =
          display.push({
            currencySymbol: rate.currency.symbol,
            name: rate.currency.name,
            chains: <any>[],
          }) - 1;
      display[existing].chains.push({
        id: rate.id,
        chain: rate.currency.chain,
        currencySymbol: rate.currency.symbol,
        value: rate.value,
        limit: rate.limit,
      });
    }
    return display;
  }

  toNetwork(chain: string): string {
    return (
      {
        eth: "ERC-20",
        bsc: "BEP-20",
        tron: "TRC-20",
      }[chain] || ""
    );
  }

  show(currencySymbol: string) {
    this.dropdowns[currencySymbol] = !this.dropdowns[currencySymbol];
    this.$forceUpdate();
  }

  showKycDialog() {
    if (!this.kycRecord && this.kycRequiredStatus) {
      this.showDialog = true;
    }
  }

  closeKycDialog() {
    this.showDialog = false;
  }

  get formattedRates() {
    return this.rates
      .sort((a, b) => (a.currency.name < b.currency.name ? -1 : 1))
      .map((a) => {
        if (a.currency.name.startsWith("Bitcoin")) {
          a.currency.name = "Bitcoin";
        }
        return a;
      });
  }

  get barColor() {
    let value = this.barValue;
    if (value < 50) {
      return (this.bgColor = "blue lighten-5"), (this.color = "blue darken-2");
    } else if (value >= 50 && value < 70) {
      return (
        (this.bgColor = "green lighten-5"), (this.color = "green darken-2")
      );
    } else {
      return (
        (this.bgColor = "orange lighten-5"), (this.color = "orange darken-2")
      );
    }
  }

  get rankIcon() {
    let value = this.barValue;
    if (value < 50) {
      return "mdi-star";
    } else if (value >= 50 && value < 70) {
      return "mdi-trophy-award";
    } else {
      return "mdi-gold";
    }
  }

  @throwsNetworkError()
  async getAnnouncement() {
    this.message = "This is the message";
    await this.announcementClient.getAnnouncement();
  }

  @throwsNetworkError()
  async getCustomerRank() {
    this.customerRank = await this.ranksClient.getCustomerRank(
      this.customer.accountId
    );
    this.maxRangeValue = this.customerRank.highestHigherAmountRange;
    this.usdtValue = this.customerRank.totalTradesUSDT;
    this.barValue = (this.usdtValue / this.maxRangeValue) * 99;
  }

  @throwsNetworkError()
  async fetchKycRecord() {
    const response = await this.kycClient.fetchKycRecord(
      this.customer.accountId
    );
    return response;
  }

  @throwsNetworkError()
  async getCustomer() {
    return await this.authClient.getCustomer(this.customer.accountId);
  }

  async mounted() {
    await Promise.all([
      this.getAnnouncement(),
      this.getRates(),
      this.getCustomerRank(),
      this.fetchKycRecord(),
      this.getCustomer(),
    ]);
    this.showKycDialog();
  }
}
